/**
 * Makes the page behind an open modal dialog inert.
 *
 * App pop-ups (Klaviyo forms, the Rebuy Smart Cart, Boost, accessiBe) mark
 * their dialog aria-modal="true" and move focus into it, but leave the rest of
 * the page in the accessibility tree. iOS VoiceOver does not confine itself to
 * an aria-modal dialog, so swiping from the pop-up's fields ran straight on
 * into the page underneath, and the close button was hard to get back to.
 *
 * While a dialog is open, everything outside it is made inert (out of the
 * accessibility tree, focus order and hit testing); when it closes, only the
 * elements this guard made inert are released. Native <dialog> elements
 * opened with showModal() are already modal and are left to the browser.
 */
const SKIPPED_TAGS = ['SCRIPT', 'STYLE', 'LINK', 'NOSCRIPT', 'TEMPLATE'];

// The attributes apps flip to show or hide a dialog (or a wrapper around it)
const WATCHED_ATTRIBUTES = ['aria-hidden', 'aria-modal', 'class', 'hidden', 'open', 'style'];

class ModalBackgroundGuard extends HTMLElement {
  connectedCallback() {
    this.inerted = new Set();
    this.scheduled = false;
    this.lastFocused = null;
    this.focusPending = false;

    /**
     * Synced straight away (a microtask after the change) so the page is
     * released before a closing dialog's own focus-return code runs in its
     * next microtask or frame: focus cannot land on an inert element. Synced
     * again on the next frame and when a transition ends, because a dialog
     * that slides in is still off screen when its attributes first change.
     */
    this.observer = new MutationObserver((records) => {
      if (!records.some((record) => this.affectsModals(record))) return;
      this.sync();
      this.schedule();
    });
    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: WATCHED_ATTRIBUTES,
    });

    this.onSettled = () => this.schedule();
    document.addEventListener('transitionend', this.onSettled, true);
    document.addEventListener('animationend', this.onSettled, true);

    // Where focus goes back to if the app closing its dialog does not return it
    this.onFocusIn = (event) => {
      if (event.target.closest('[aria-modal="true"]')) return;
      this.lastFocused = event.target;
      this.focusPending = false;
    };
    document.addEventListener('focusin', this.onFocusIn);

    this.sync();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    document.removeEventListener('transitionend', this.onSettled, true);
    document.removeEventListener('animationend', this.onSettled, true);
    document.removeEventListener('focusin', this.onFocusIn);
    clearTimeout(this.focusPendingTimer);
    this.release();
  }

  // Skips the high-frequency style writes of marquees and sliders unless the
  // element written to holds (or is) a dialog.
  affectsModals(record) {
    if (record.type === 'childList') return true;
    const target = record.target;
    return target.matches('[aria-modal="true"]') || !!target.querySelector('[aria-modal="true"]');
  }

  schedule() {
    if (this.scheduled) return;
    this.scheduled = true;
    requestAnimationFrame(() => {
      this.scheduled = false;
      this.sync();
    });
  }

  inViewport(element) {
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.right > 0 && rect.bottom > 0 && rect.left < window.innerWidth && rect.top < window.innerHeight;
  }

  // Closed app dialogs stay in the DOM: hidden with aria-hidden, display or
  // visibility, or parked off screen. A dialog counts as open only when none
  // of those apply, so a closed drawer can never lock the page. Its children
  // are measured too: the Rebuy cart's dialog box has no size of its own,
  // only its fixed-position panel does.
  isOpen(dialog) {
    if (dialog instanceof HTMLDialogElement) return false;
    if (dialog.closest('[aria-hidden="true"], [hidden]')) return false;

    const style = getComputedStyle(dialog);
    if (style.display === 'none' || style.visibility === 'hidden') return false;

    return [dialog, ...dialog.children].some((element) => this.inViewport(element));
  }

  openDialog() {
    return [...document.querySelectorAll('[aria-modal="true"]')].find((dialog) => this.isOpen(dialog));
  }

  /**
   * Everything beside the dialog and its ancestors, at every level up to
   * <body>, starting with the dialog's own siblings. A dialog rendered inside
   * the page (the product media zoom sits within <main>, the pickup drawer
   * beside its open button) would otherwise leave the rest of its own section
   * reachable. Siblings marked data-modal-support (a backdrop that closes the
   * dialog on tap) stay live: inert would also stop them being clicked.
   */
  backgroundOf(dialog) {
    const background = new Set();
    for (let node = dialog; node && node !== document.body; node = node.parentElement) {
      for (const sibling of node.parentElement?.children || []) {
        if (sibling === node || sibling === this || SKIPPED_TAGS.includes(sibling.tagName) || sibling.hasAttribute('data-modal-support')) continue;
        background.add(sibling);
      }
    }
    return background;
  }

  release(keep = new Set()) {
    this.inerted.forEach((element) => {
      if (keep.has(element)) return;
      element.inert = false;
      this.inerted.delete(element);
    });
  }

  /**
   * Klaviyo and Rebuy leave focus on nothing after closing, or on their close
   * button inside the now-hidden dialog until its exit animation drops it.
   * Retried on later syncs (next frame, transition end) until focus is
   * somewhere real again.
   */
  restoreFocus() {
    const active = document.activeElement;
    // Only called with no dialog open, so focus still inside one is focus in a
    // closing dialog: Rebuy's close button stays focused while its cart slides
    // out, then is hidden with visibility: hidden (which checkVisibility() ignores).
    const focusLost =
      !active ||
      active === document.body ||
      !!active.closest('[aria-modal="true"]') ||
      !!active.closest('[aria-hidden="true"], [inert]') ||
      getComputedStyle(active).visibility === 'hidden' ||
      active.getClientRects().length === 0;
    if (!focusLost) {
      this.focusPending = false;
      return;
    }

    const target = this.lastFocused;
    if (!target?.isConnected || target.closest('[inert]')) return;
    target.focus({ preventScroll: true });
    if (document.activeElement === target) this.focusPending = false;
  }

  sync() {
    const dialog = this.openDialog();
    const background = dialog ? this.backgroundOf(dialog) : new Set();
    if (!dialog && this.inerted.size > 0) {
      this.focusPending = true;
      // Only the close itself is followed up; later focus loss is not ours
      clearTimeout(this.focusPendingTimer);
      this.focusPendingTimer = setTimeout(() => (this.focusPending = false), 1000);
    }
    this.release(background);
    if (dialog) this.focusPending = false;
    else if (this.focusPending) this.restoreFocus();

    // Elements already inert were made so by someone else; they stay theirs
    background.forEach((element) => {
      if (element.inert) return;
      element.inert = true;
      this.inerted.add(element);
    });
  }
}

if (!customElements.get('modal-background-guard')) {
  customElements.define('modal-background-guard', ModalBackgroundGuard);
}
